import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  activeFilterCount,
  type CatalogFilters,
  labelMatches,
  matchesFilters,
  type ResourceKind,
} from '@/entities/catalog';
import type { AccessibleBoard } from '@/entities/collab';
import { readRecentBoardIds } from '@/entities/board';
import { type Database, readRecentDatabaseIds } from '@/entities/database';
import { readRecentDocumentIds, type WorkspaceDocument } from '@/entities/document';
import { type GroupChild, groupAddress, type WorkspaceGroup } from '@/entities/workspace-group';
import { useCopyLink } from '@/features/resource-nav';
import { GroupCard } from '@/features/workspace-groups';
import { EmptyState } from '@/shared/ui';

import styles from './HomeDashboard.module.css';
import { SectionHead } from './SectionHead';
import {
  boardFilterable,
  catalogLink,
  databaseFilterable,
  documentFilterable,
  HOME_OFFERED,
} from './home-filters';
import type { HomePlaces } from '@/entities/places';

const SHOWN = 12;

interface Member {
  kind: ResourceKind;
  id: string;
  name: string;
  groupId: string | null | undefined;
  passes: boolean;
}

/**
 * The groups of every workspace as navigational entry points (they open in their own workspace;
 * nothing is copied). Untouched, Home lists the top-level groups; with a search or a filter it
 * lists the groups of ANY depth that directly hold a matching resource, or whose name matches, so
 * something several levels down is still reachable and the card's location says where.
 */
export function HomeGroups({
  places,
  boards,
  databases,
  documents,
  query,
  filters,
  userId,
  full = false,
}: {
  places: HomePlaces;
  boards: readonly AccessibleBoard[];
  databases: readonly Database[];
  documents: readonly WorkspaceDocument[];
  query: string;
  filters: CatalogFilters;
  userId: string | null;
  /** The complete catalog page: every match, no section header. */
  full?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const copyLink = useCopyLink();
  const { groups } = places;

  const { items, childrenOf } = useMemo(() => {
    const recentBoards = new Set(userId ? readRecentBoardIds(userId) : []);
    const recentDatabases = new Set(userId ? readRecentDatabaseIds(userId) : []);
    const recentDocuments = new Set(userId ? readRecentDocumentIds(userId) : []);
    const test = (item: Parameters<typeof matchesFilters>[0]) =>
      matchesFilters(item, filters, userId, query, HOME_OFFERED);
    const members: Member[] = [
      ...boards.map((b) => ({
        kind: 'board' as const,
        id: b.id,
        name: b.name,
        groupId: b.group_id,
        passes: test(boardFilterable(b, recentBoards)),
      })),
      ...databases.map((d) => ({
        kind: 'database' as const,
        id: d.id,
        name: d.name,
        groupId: d.group_id,
        passes: test(databaseFilterable(d, recentDatabases, places.spaces, places.names)),
      })),
      ...documents.map((d) => ({
        kind: 'document' as const,
        id: d.id,
        name: d.name,
        groupId: d.group_id,
        passes: test(documentFilterable(d, recentDocuments, places.spaces, places.locationOf(d))),
      })),
    ];
    // the preview shows everything the viewer may see directly inside, whatever the filters say
    const children = new Map<string, GroupChild[]>();
    const add = (group: string | null | undefined, child: GroupChild) => {
      if (group) children.set(group, [...(children.get(group) ?? []), child]);
    };
    for (const g of groups) add(g.parent_group_id, { kind: 'group', id: g.id, name: g.name });
    for (const m of members) add(m.groupId, { kind: m.kind, id: m.id, name: m.name });

    const holding = new Set(members.filter((m) => m.passes && m.groupId).map((m) => m.groupId));
    const needle = query.trim().toLocaleLowerCase();
    const plainFilters =
      filters.type === 'all' &&
      !filters.favorites &&
      !filters.recent &&
      filters.owner === 'any' &&
      filters.labels.length === 0;
    const narrowed = needle !== '' || activeFilterCount(filters, HOME_OFFERED) > 0;
    const shown = groups.filter((g) => {
      if (!narrowed) return g.parent_group_id === null;
      const space = places.spaces.get(g.workspace_id);
      const spaceOk = filters.space === 'any' || filters.space === space;
      const byName =
        needle !== '' &&
        plainFilters &&
        spaceOk &&
        `${g.name} ${places.groupLocation(g)}`.toLocaleLowerCase().includes(needle);
      // a label filter also finds the group that carries the label ITSELF (never its contents)
      const byLabel = filters.labels.length > 0 && labelMatches(filters.labels, g.labels ?? []);
      const labelText = (g.labels ?? []).map((l) => l.name).join(' ');
      const byLabelName =
        needle !== '' && plainFilters && spaceOk && labelText.toLocaleLowerCase().includes(needle);
      return holding.has(g.id) || byName || byLabel || byLabelName;
    });
    return { items: shown, childrenOf: children, narrowed };
  }, [groups, boards, databases, documents, filters, query, userId, places]);

  const narrowed = query.trim() !== '' || activeFilterCount(filters, HOME_OFFERED) > 0;
  if (!places.ready) return null;
  // Never an empty block for someone with no groups at all.
  if (groups.length === 0) return null;
  const sorted = items.toSorted(
    (a, b) =>
      places.groupLocation(a).localeCompare(places.groupLocation(b)) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
  const open = (group: WorkspaceGroup) => {
    const params = new URLSearchParams({ group: group.id });
    if (group.section_id) params.set('folder', group.section_id);
    void navigate(`/app/workspaces/${group.workspace_id}?${params.toString()}`);
  };
  return (
    <section
      className={styles.databases}
      aria-labelledby="home-groups-title"
      data-testid="home-groups"
    >
      {!full && (
        <SectionHead
          id="home-groups-title"
          title={t('groups.sectionTitle')}
          count={sorted.length}
          to={catalogLink('/app/browse/groups', query)}
          linkLabel={t('home.viewAllGroups')}
          linkTestId="all-groups-link"
        />
      )}
      {sorted.length === 0 ? (
        <EmptyState title={narrowed ? t('groups.noMatches') : t('groups.noneYet')} />
      ) : (
        <ul className={styles.groupGrid}>
          {(full ? sorted : sorted.slice(0, SHOWN)).map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              items={childrenOf.get(group.id) ?? []}
              location={places.groupLocation(group)}
              actions={{
                onOpen: () => {
                  open(group);
                },
                onCopyLink: () => {
                  copyLink(groupAddress(group));
                },
              }}
            />
          ))}
        </ul>
      )}
      {!full && sorted.length > SHOWN && (
        <p className={styles.moreNote}>
          {t('groups.moreOnHome', { count: sorted.length - SHOWN })}
        </p>
      )}
    </section>
  );
}
